const MAX_DEPTH = 8;

export function errorToObject(err: unknown): {
  name?: string;
  message?: string;
  stack?: string;
} {
  if (err instanceof Error) {
    const out: { name?: string; message?: string; stack?: string } = {};
    if (err.name) out.name = err.name;
    if (err.message) out.message = err.message;
    if (typeof err.stack === 'string') out.stack = err.stack;
    return out;
  }
  return {};
}

export function safeStringify(value: unknown, byteBudget: number): string {
  const seen = new WeakSet<object>();

  function replace(_key: string, val: unknown): unknown {
    if (val === null || val === undefined) return val;
    const t = typeof val;
    if (t === 'function') return '[Function]';
    if (t === 'symbol') return val.toString();
    if (t === 'bigint') return `${(val as bigint).toString()}n`;
    if (t !== 'object') return val;

    if (val instanceof Error) {
      return { name: val.name, message: val.message, stack: val.stack };
    }
    if (val instanceof Date) return val.toISOString();
    if (val instanceof RegExp) return val.toString();
    if (val instanceof Map) {
      const obj: Record<string, unknown> = {};
      for (const [k, v] of val) obj[String(k)] = v;
      return { __map__: obj };
    }
    if (val instanceof Set) return { __set__: Array.from(val) };
    if (typeof DOMException !== 'undefined' && val instanceof DOMException) {
      return { name: val.name, message: val.message };
    }

    if (seen.has(val as object)) return '[Circular]';
    seen.add(val as object);
    return val;
  }

  let out: string;
  try {
    out = JSON.stringify(value, replace) ?? 'null';
  } catch {
    try {
      out = String(value);
    } catch {
      out = '[Unserializable]';
    }
  }
  return truncateBytes(out, byteBudget);
}

export function truncateBytes(s: string, byteBudget: number): string {
  if (byteBudget <= 0) return '';
  if (s.length <= byteBudget) return s;
  const sliced = s.slice(0, byteBudget);
  return `${sliced}…[truncated ${s.length - byteBudget} chars]`;
}

export function coerceMessage(value: unknown, maxLen: number): string {
  let str: string;
  if (typeof value === 'string') str = value;
  else if (value instanceof Error) {
    const name = value.name || 'Error';
    const msg = value.message || '';
    str = msg ? `${name}: ${msg}` : name;
  } else if (value === undefined) str = 'undefined';
  else if (value === null) str = 'null';
  else {
    try {
      str = JSON.stringify(value) ?? String(value);
    } catch {
      str = '[Unserializable]';
    }
  }
  if (str.length > maxLen) return `${str.slice(0, maxLen)}…[truncated]`;
  return str;
}

export { MAX_DEPTH };
